import '../styles/globals.css';
import { useState, useEffect, useCallback } from 'react';
import { RegistrationForm } from '../components/registration-form';
import { Login } from '../components/login';
import { AdminDashboard } from '../components/admin-dashboard';
import { AuthProvider, useAuth } from '../components/auth-context';
import { Navbar } from '../components/navbar';
import { RegistrationSlimHeader } from '../components/registration-slim-header';
import { StatusTrackingView } from '../components/status-tracking-view';
import { Toaster } from '../components/ui/sonner';
import { MagicLinkPortal } from '../components/speaker/MagicLinkPortal';
import { InviteAcceptancePortal } from '../components/invite/InviteAcceptancePortal';
import { MaintenanceMode } from '../components/public/MaintenanceMode';
import { EvaluatorPortal } from './components/evaluator/EvaluatorPortal';
import { getPublicSettings } from './api/settings';
import { buildPath, matchPath, matchPathStartsWith, stripBasePath } from './lib/routing';

type Page = 'register' | 'login' | 'admin' | 'tracking' | 'speaker-upload' | 'invite' | 'evaluator-portal';
type PublicHeaderPage = 'register' | 'login' | 'tracking' | 'speaker-upload' | 'invite';

interface PublicMaintenanceState {
  mode: boolean;
  message: string;
}

function parseBooleanValue(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
}

function AppContent() {
  const getInviteTokenFromPath = (): string => {
    const relativePath = stripBasePath(window.location.pathname);
    if (!relativePath.startsWith('/invite/')) return '';
    return relativePath.replace('/invite/', '').trim();
  };

  const getEvaluatorTokenFromPath = (): string => {
    const relativePath = stripBasePath(window.location.pathname);
    if (!relativePath.startsWith('/evaluator/')) return '';
    return decodeURIComponent(relativePath.replace('/evaluator/', '').trim());
  };

  const detectInitialPage = (): Page => {
    const pathname = window.location.pathname;
    if (matchPathStartsWith(pathname, '/evaluator/')) return 'evaluator-portal';
    if (matchPathStartsWith(pathname, '/invite/')) return 'invite';
    if (matchPath(pathname, '/speaker/upload')) return 'speaker-upload';
    if (matchPathStartsWith(pathname, '/admin')) return 'admin';
    return 'register';
  };

  const [currentPage, setCurrentPage] = useState<Page>(detectInitialPage);
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [registerKey, setRegisterKey] = useState(0);
  const [maintenanceState, setMaintenanceState] = useState<PublicMaintenanceState>({ mode: false, message: '' });
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);

  const handleNavigation = (page: Page) => {
    if (page === 'admin' && !isAuthenticated) {
      setCurrentPage('login');
      return;
    }

    if (page === 'register') {
      setRegisterKey((prev) => prev + 1);
    }

    setCurrentPage(page);
  };

  const handleLogout = () => {
    logout();
    setCurrentPage('register');
    setRegisterKey((prev) => prev + 1);
  };

  const handleBackToRegister = useCallback(() => {
    if (matchPathStartsWith(window.location.pathname, '/evaluator/')) {
      window.sessionStorage.removeItem('ciu:evaluator-token');
      window.history.replaceState(null, '', buildPath('/'));
    }
    setCurrentPage('register');
    setRegisterKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        if (currentPage !== 'admin' && currentPage !== 'evaluator-portal') {
          setCurrentPage('admin');
        }
      } else if (currentPage === 'admin') {
        setCurrentPage('login');
      }
    }
  }, [isLoading, isAuthenticated, currentPage]);

  useEffect(() => {
    const fetchPublicSettings = async () => {
      try {
        const settings = await getPublicSettings();
        const mode = parseBooleanValue(settings['system.maintenance.mode']);
        const messageValue = settings['system.maintenance.message'];
        const message = typeof messageValue === 'string' ? messageValue : '';
        setMaintenanceState({ mode, message });
      } catch (error) {
        console.error('Error cargando configuracion publica:', error);
        setMaintenanceState({ mode: false, message: '' });
      } finally {
        setIsLoadingPublicSettings(false);
      }
    };

    fetchPublicSettings();
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex animate-pulse flex-col items-center">
          <div className="mb-4 h-8 w-8 rounded-full bg-blue-600"></div>
          <div className="text-xl font-semibold text-gray-700">Cargando aplicacion...</div>
        </div>
      </div>
    );
  }

  const pagesGuardedByMaintenance: Page[] = ['register', 'tracking', 'speaker-upload'];
  const isMaintenanceGuardedPage = pagesGuardedByMaintenance.includes(currentPage);
  const isPublicHeaderPage = currentPage === 'register' || currentPage === 'login' || currentPage === 'tracking' || currentPage === 'speaker-upload' || currentPage === 'invite';

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {isPublicHeaderPage ? (
        <RegistrationSlimHeader
          currentPage={currentPage as PublicHeaderPage}
          isAuthenticated={isAuthenticated}
          onNavigate={handleNavigation}
          onLogout={handleLogout}
        />
      ) : currentPage !== 'admin' && currentPage !== 'evaluator-portal' ? (
        <Navbar
          currentPage={currentPage}
          isAuthenticated={isAuthenticated}
          userName={user?.name}
          onNavigate={handleNavigation}
          onLogout={handleLogout}
        />
      ) : null}

      {currentPage === 'admin' && <AdminDashboard onBackToRegister={handleBackToRegister} />}
      {currentPage === 'evaluator-portal' && (
        <EvaluatorPortal token={getEvaluatorTokenFromPath()} onBackToRegister={handleBackToRegister} />
      )}

      {currentPage !== 'admin' && currentPage !== 'evaluator-portal' && (
        <main className="container mx-auto max-w-7xl flex-grow animate-in px-4 py-8 fade-in duration-500">
          {isMaintenanceGuardedPage && isLoadingPublicSettings && (
            <div className="flex min-h-[50vh] items-center justify-center">
              <div className="flex items-center gap-3 text-[#0f2744]">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#0f2744] border-t-transparent" />
                <span className="font-medium">Cargando configuracion del sistema...</span>
              </div>
            </div>
          )}

          {isMaintenanceGuardedPage && !isLoadingPublicSettings && maintenanceState.mode && (
            <MaintenanceMode message={maintenanceState.message} />
          )}

          {!isMaintenanceGuardedPage || (!isLoadingPublicSettings && !maintenanceState.mode) ? (
            <>
              {currentPage === 'register' && <RegistrationForm key={registerKey} />}
              {currentPage === 'login' && (
                <Login
                  onBackToRegister={() => {
                    setCurrentPage('register');
                    setRegisterKey((prev) => prev + 1);
                  }}
                />
              )}
              {currentPage === 'tracking' && <StatusTrackingView onBackToRegister={() => setCurrentPage('register')} />}
              {currentPage === 'speaker-upload' && <MagicLinkPortal />}
              {currentPage === 'invite' && <InviteAcceptancePortal token={getInviteTokenFromPath()} />}
            </>
          ) : null}
        </main>
      )}

      {currentPage !== 'admin' && currentPage !== 'evaluator-portal' && (
        <footer className={`mt-auto border-t py-6 ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'border-[#c9a227] bg-[#0f2744] text-gray-300' : 'border-gray-200 bg-white text-gray-500'}`}>
          <div className="container mx-auto px-4 text-center text-sm">
            <p className="mb-2">
              Desarrollado por la <span className={`font-semibold ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'text-white' : 'text-blue-700'}`}>DTIE - Universidad Autonoma de Chiapas</span>
            </p>
            <div className={`flex justify-center space-x-4 text-xs ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'text-gray-400' : 'text-gray-500'}`}>
              <a href="#" className={`transition-colors ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'hover:text-white' : 'hover:text-gray-800'}`}>Terminos y Condiciones</a>
              <span>|</span>
              <a href="#" className={`transition-colors ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'hover:text-white' : 'hover:text-gray-800'}`}>Aviso de Privacidad</a>
              <span>|</span>
              <a href="#" className={`transition-colors ${['register', 'tracking', 'login', 'speaker-upload', 'invite'].includes(currentPage) ? 'hover:text-white' : 'hover:text-gray-800'}`}>Soporte</a>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
      <Toaster />
    </AuthProvider>
  );
}
